'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { handleAuthError as handleAuthErrorBase } from '@/lib/auth-error-handler';
import {
  getCredentialRecoveryMessage,
  parseCredentialRecoveryReason,
  reduceCredentialRecoveryReason,
  type CredentialRecoveryReason,
} from '@/lib/auth/credential-recovery-error';
import { clearPendingAuthPassword } from '@/lib/auth/pending-auth-password';
import { getAuthPageCopy } from '@/i18n/auth-page-copy';
import {
  buildReactivationAuthUrl,
  clearStoredReactivationEmail,
  getStoredReactivationEmail,
  persistReactivationEmail,
  resolvePendingReactivationEmail,
} from '@/lib/reactivation-state';
import {
  safeSessionStorageGetItem,
  safeSessionStorageRemoveItem,
  safeSessionStorageSetItem,
} from '@/lib/utils';
import { useRouter, useSearchParams } from 'next/navigation';
import { useLocale } from 'next-intl';
import { toast } from 'sonner';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isLikelyEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

const SELF_DEACTIVATED_ACCOUNT_ERROR_CODE = 'SELF_DEACTIVATED_ACCOUNT';

export type SignInFormType = {
  email: string;
  password: string;
};

// OTP handled via local state, not react-hook-form

export interface SignInFormProps {
  onToggleMode: () => void;
  onPasswordReset: () => void;
  onPhoneAuth: () => void;
  inviteToken?: string | null;
  authBasePath?: string;
}
export function useSignInState({
  onToggleMode,
  onPasswordReset,
  inviteToken,
  authBasePath = '/auth',
}: SignInFormProps) {
  const router = useRouter();

  const searchParams = useSearchParams();

  const locale = useLocale();

  const copy = getAuthPageCopy(locale);

  const handleAuthError = (error: unknown) =>
    handleAuthErrorBase(error, {
      emailNotVerified: copy.pleaseVerifyEmail,
      invalidCredentials: copy.invalidCredentials,
      invalidPassword: copy.invalidPasswordTryAgain,
      invalidVerificationCode: copy.invalidVerificationCode,
      verificationCodeExpired: copy.verificationCodeExpired,
    });

  const signInSchema = z.object({
    email: z.string().email(copy.emailValidation),
    password: z.string().min(1, copy.passwordValidation),
  });

  const returnTo = searchParams.get('returnTo');

  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const [credentialRecoveryReason, setCredentialRecoveryReason] = useState(() =>
    parseCredentialRecoveryReason(searchParams.get('authError'))
  );

  const [pendingMethod, setPendingMethod] = useState<
    'credentials' | 'google' | null
  >(null);

  // Get step from URL, default to 'signIn'
  const stepParam = searchParams.get('step');

  const step =
    stepParam === 'otp'
      ? 'otp'
      : stepParam === 'reactivate'
        ? 'reactivate'
        : 'signIn';

  const reactivationPhase =
    searchParams.get('phase') === 'otp' ? 'otp' : 'prompt';

  const deactivationNotice = searchParams.get('deactivation');

  const emailParam = searchParams.get('email');

  const normalizedEmailParam = emailParam?.trim().toLowerCase() ?? '';

  const [reactivationEmail, setReactivationEmail] = useState(() =>
    resolvePendingReactivationEmail(
      null,
      step === 'reactivate' ? emailParam : null
    )
  );

  const pendingEmail =
    step === 'reactivate'
      ? reactivationEmail
      : isLikelyEmail(normalizedEmailParam)
        ? normalizedEmailParam
        : '';

  const [otpCode, setOtpCode] = useState('');

  const isOtpCodeComplete = otpCode.length === 6;

  const [pendingPassword, setPendingPassword] = useState('');

  const otpContainerRef = useRef<HTMLDivElement | null>(null);

  const form = useForm<SignInFormType>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: pendingEmail || '',
      password: '',
    },
  });

  // Update form email when URL changes
  React.useEffect(() => {
    if (pendingEmail && pendingEmail !== form.getValues('email')) {
      // Normalize email to lowercase when setting from URL
      form.setValue('email', pendingEmail.trim().toLowerCase());
    }
  }, [pendingEmail, form]);

  React.useEffect(() => {
    if (step !== 'reactivate') {
      return;
    }

    const nextEmail = resolvePendingReactivationEmail(
      getStoredReactivationEmail(safeSessionStorageGetItem),
      emailParam
    );

    if (nextEmail && nextEmail !== reactivationEmail) {
      setReactivationEmail(nextEmail);
    }

    if (emailParam && nextEmail) {
      persistReactivationEmail(safeSessionStorageSetItem, nextEmail);
      router.replace(
        buildReactivationAuthUrl(
          searchParams.toString(),
          reactivationPhase === 'otp' ? 'otp' : undefined
        )
      );
    }
  }, [
    emailParam,
    reactivationEmail,
    reactivationPhase,
    router,
    searchParams,
    step,
  ]);

  const goToReactivateStep = (email: string, phase?: 'otp') => {
    const normalizedEmail = persistReactivationEmail(
      safeSessionStorageSetItem,
      email
    );
    setReactivationEmail(normalizedEmail);
    router.push(buildReactivationAuthUrl(searchParams.toString(), phase));
  };

  const goToSignInStep = () => {
    clearStoredReactivationEmail(safeSessionStorageRemoveItem);
    setReactivationEmail('');
    const params = new URLSearchParams(searchParams.toString());
    params.delete('step');
    params.delete('email');
    params.delete('phase');
    params.delete('error');
    params.delete('error_description');
    router.push(`/auth?${params.toString()}`);
  };

  const applyFieldErrors = (nextFieldErrors: Record<string, string>) => {
    for (const [fieldName, message] of Object.entries(nextFieldErrors)) {
      if (fieldName === 'email' || fieldName === 'password') {
        form.setError(fieldName, { type: 'server', message });
      }
    }
  };

  const returnToCredentialsWithPasswordError = (
    reason: CredentialRecoveryReason
  ) => {
    clearPendingAuthPassword();
    setPendingPassword('');
    setCredentialRecoveryReason((current) =>
      reduceCredentialRecoveryReason(current, { type: 'show', reason })
    );
    const params = new URLSearchParams(searchParams.toString());
    params.delete('step');
    if (pendingEmail) {
      params.set('email', pendingEmail);
    }
    params.set('authError', reason);
    router.push(`${authBasePath}?${params.toString()}`);
    toast.error(getCredentialRecoveryMessage(reason, copy));
  };

  const credentialRecoveryError = credentialRecoveryReason
    ? getCredentialRecoveryMessage(credentialRecoveryReason, copy)
    : null;

  const formError = error ?? credentialRecoveryError;
  const displayedError = Object.values(form.formState.errors).some(
    (fieldError) => fieldError?.message === formError
  )
    ? null
    : formError;

  const onCredentialInput = () => {
    setError(null);
    setCredentialRecoveryReason((current) =>
      reduceCredentialRecoveryReason(current, { type: 'credential-input' })
    );
    for (const field of ['email', 'password'] as const) {
      if (form.getFieldState(field).error?.type === 'server') {
        form.clearErrors(field);
      }
    }
  };

  // Ensure OTP input is auto-focused when we enter OTP step
  React.useEffect(() => {
    if (step === 'otp') {
      // Focus the hidden input rendered by InputOTP
      setTimeout(() => {
        const input = otpContainerRef.current?.querySelector('input');
        input?.focus();
      }, 0);
    }
  }, [step]);

  React.useEffect(() => {
    if (!parseCredentialRecoveryReason(searchParams.get('authError'))) {
      return;
    }

    const params = new URLSearchParams(searchParams.toString());
    params.delete('authError');
    const query = params.toString();
    router.replace(query ? `${authBasePath}?${query}` : authBasePath);
  }, [searchParams, router, authBasePath]);
  return {
    onToggleMode,
    onPasswordReset,
    inviteToken,
    authBasePath,
    isLikelyEmail,
    isRecord,
    SELF_DEACTIVATED_ACCOUNT_ERROR_CODE,
    router,
    searchParams,
    locale,
    copy,
    handleAuthError,
    signInSchema,
    returnTo,
    isLoading,
    setIsLoading,
    error,
    setError,
    credentialRecoveryReason,
    setCredentialRecoveryReason,
    pendingMethod,
    setPendingMethod,
    stepParam,
    step,
    reactivationPhase,
    deactivationNotice,
    emailParam,
    normalizedEmailParam,
    reactivationEmail,
    setReactivationEmail,
    pendingEmail,
    otpCode,
    setOtpCode,
    isOtpCodeComplete,
    pendingPassword,
    setPendingPassword,
    otpContainerRef,
    form,
    goToReactivateStep,
    goToSignInStep,
    applyFieldErrors,
    returnToCredentialsWithPasswordError,
    credentialRecoveryError,
    displayedError,
    onCredentialInput,
  };
}
